import React from 'react';

import get from 'lodash.get';
import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { Observable, concat, of, race, EMPTY, merge } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';

import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { ErrorModal } from '../../components/modals/error-modal';
import { ShipTogetherDestinationModal } from '../../components/modals/ship-together-destination-modal';
import { ShipTogetherHelperService } from '../../services/ship-together-helper-service';
import { orderGridExpandRow } from '../orders/grid/orders.grid.actions';
import { goToOrderDetails } from '../orders/order-details/orders.details.actions';
import { silentRequestGetOrders, GET_ORDERS_SUCCESS, GET_ORDERS_FAILURE } from '../orders/orders.actions';
import { messages } from './messages';
import * as Actions from './ship-together.actions';
import { GET_ALERT_DETAILS_SUCCESS, GET_ALERT_DETAILS_FAILURE } from '../alerts/details/alerts.details.actions';
import { ORDERS_GET_COUNTS_SUCCESS } from '../orders/nav/orders.nav.actions';

const messageMap = {
  "'Sales Order Id' should not be empty.": messages.salesOrderIdEmpty,
  "'Quantity' must be greater than or equal to '1'.": messages.quantityGreaterThanOne,
};

const getCombineOrdersFailError = (err) => {
  const errorMessage = messageMap[err] || messages.fallbackErrorMessageForFailedCombine;
  return <FormattedMessage {...errorMessage} />;
};

const shipSalesOrderTogetherFailure$ = (modalId, error) => {
  ModalService.closeModalById(modalId);

  const notificationProps = {
    title: <FormattedMessage {...messages.shipTogetherExistsErrorTitle} />,
    message: getCombineOrdersFailError(error.Message),
  };
  ModalService.launchModal({
    component: ErrorModal,
    props: notificationProps,
  });
  return EMPTY;
};

const shipSalesOrdersTogetherSuccess$ = (action$, action, modalId) => {
  const fulfillmentPlanId = get(action, 'response.fulfillmentPlanId');
  if (!fulfillmentPlanId) {
    return EMPTY;
  }

  return merge(
    of(silentRequestGetOrders()),
    race(
      action$.pipe(
        ofType(GET_ORDERS_SUCCESS, GET_ALERT_DETAILS_SUCCESS, ORDERS_GET_COUNTS_SUCCESS),
        take(1),
        mergeMap(() => {
          const selectedOrderId = get(action, 'selectedOrderId');
          ModalService.closeModalById(modalId);
          return of(
            goToOrderDetails({
              salesOrderId: selectedOrderId,
              selectOrderInGrid: false,
              fulfillmentPlanId,
            }),
            orderGridExpandRow(fulfillmentPlanId),
          );
        }),
      ),
      action$.pipe(
        ofType(GET_ORDERS_FAILURE, GET_ALERT_DETAILS_FAILURE),
        take(1),
        mergeMap(() => {
          const refresh = (text) => (
            // oxlint-disable-next-line jsx-a11y/anchor-is-valid
            <a href='' onClick={() => window.location.reload()}>
              {text}
            </a>
          );

          const reloadDataErrorNotification = {
            content: (
              <FormattedMessage
                {...messages.shipTogetherSucceededButRefreshFailed}
                values={{
                  refresh,
                }}
              />
            ),
            notificationType: NOTIFICATION_TYPE_ERROR,
            dontHide: true,
          };
          NotificationService.showNotification(reloadDataErrorNotification);
          return EMPTY;
        }),
      ),
    ).pipe(mergeMap((next) => of(next))),
  );
};

const launchDestinationModal = (observer, selectedFulfillmentPlans, selectedSalesOrders) => {
  const { fulfillmentPlansWithCombineDetails, shipTogetherItems, fulfillmentPlanIdsWithoutItems, combinedTotalItemQuantity } =
    ShipTogetherHelperService.shipTogetherOrderMeta(selectedFulfillmentPlans, selectedSalesOrders);

  const modalId = ModalService.launchModal({
    component: ShipTogetherDestinationModal,
    props: {
      onShipTogetherContinue: (selectedFulfillmentPlanId, selectedOrderId) => {
        observer.next(
          Actions.shipSalesOrdersTogether(selectedFulfillmentPlanId, selectedOrderId, shipTogetherItems, fulfillmentPlanIdsWithoutItems),
        );
        observer.complete();
      },
      fulfillmentPlansWithCombineDetails,
      combinedTotalItemQuantity,
    },
    onClose: () => observer.complete(),
  });
  return modalId;
};

export const proceedWithShipTogether$ = (action$, selectedFulfillmentPlans, selectedSalesOrders) => {
  let modalId;

  return new Observable((observer) => {
    modalId = launchDestinationModal(observer, selectedFulfillmentPlans, selectedSalesOrders);
  }).pipe(
    mergeMap((nextAction) => {
      if (nextAction.type === Actions.SHIP_SALES_ORDERS_TOGETHER_REQUEST) {
        return concat(
          of(nextAction),
          race(
            action$.pipe(
              ofType(Actions.SHIP_SALES_ORDERS_TOGETHER_SUCCESS),
              take(1),
              mergeMap((action) => shipSalesOrdersTogetherSuccess$(action$, action, modalId)),
            ),
            action$.pipe(
              ofType(Actions.SHIP_SALES_ORDERS_TOGETHER_FAILURE),
              take(1),
              mergeMap((action) => shipSalesOrderTogetherFailure$(modalId, action.err)),
            ),
          ),
        );
      }
      return of(nextAction);
    }),
  );
};
